import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useState, useEffect } from 'react';
import { 
  FirstAidKit, 
  Heartbeat,
  House, 
  ClipboardText, 
  Calendar, 
  ChatCircle, 
  SignOut,
  Users,
  ChartBar,
  Robot,
  UserCircle,
  Queue,
  Shield,
  ShieldCheck,
  Pill,
  Warning,
  CreditCard,
  Bell,
  FolderOpen,
  Microphone,
  Receipt,
  Rocket,
  Flask,
  VideoCamera,
  Folders,
  MagnifyingGlass,
  FileText,
  CaretDown,
  CaretRight,
  CurrencyDollar,
  ClockCounterClockwise,
  PaperPlaneTilt,
  XCircle,
  CalendarBlank,
  Upload,
  CheckSquare,
  IdentificationCard,
  Heart,
  Watch
} from '@phosphor-icons/react';

// Streamlined patient links - focused on appointment workflow
const patientLinks = [
  { to: '/patient/appointments', icon: Calendar, label: 'Appointments', end: true },
  { to: '/patient/ai-cma', icon: Robot, label: 'Virtual Medical Assistant' },
  { to: '/patient/health-devices', icon: Watch, label: 'Connected Devices' },
  { to: '/patient/home-labs', icon: Flask, label: 'At-Home Lab Tests' },
  { to: '/patient/insurance-id', icon: IdentificationCard, label: 'Insurance / ID / Consent' },
  { to: '/patient/intake', icon: ClipboardText, label: 'Health Intake / Labs' },
  { to: '/patient/refills', icon: Pill, label: 'Medication Refills' },
  { to: '/patient/documents', icon: FolderOpen, label: 'Medical Records' },
  { to: '/patient/medicare-forms', icon: Shield, label: 'Medicare ABN Forms' },
  { to: '/patient/messages', icon: ChatCircle, label: 'Messages' },
  { to: '/patient/ai-assistant', icon: Robot, label: 'AI Assistant' },
];

const cmaLinks = [
  { to: '/staff/schedule', icon: CalendarBlank, label: 'Daily Schedule', end: true },
  { to: '/cma', icon: House, label: 'Dashboard', end: true },
  { to: '/staff/patients', icon: Folders, label: 'Patient Records' },
  { to: '/staff/refills', icon: Pill, label: 'Refill Requests' },
  { to: '/staff/home-labs', icon: Flask, label: 'At-Home Lab Orders' },
  { to: '/staff/ai-checkins', icon: Robot, label: 'AI Check-In Reviews' },
  { to: '/cma/health-devices', icon: Watch, label: 'Patient Wearables' },
  { to: '/cma/checkout-queue', icon: CheckSquare, label: 'Checkout Queue' },
  { to: '/cma/triage', icon: ClipboardText, label: 'Triage' },
  { to: '/cma/video-calls', icon: VideoCamera, label: 'Telehealth Video' },
  { to: '/cma/medications', icon: Pill, label: 'Medications' },
  { to: '/cma/labs', icon: Flask, label: 'Lab Results' },
  { to: '/staff/medicare-forms', icon: Shield, label: 'Medicare Forms' },
  { to: '/cma/care-gaps', icon: Warning, label: 'Care Gaps' },
  { to: '/cma/messages', icon: ChatCircle, label: 'Messages' },
  { to: '/cma/ai-assist', icon: Robot, label: 'AI Assistant' },
  { to: '/cma/end-of-day-report', icon: CurrencyDollar, label: 'End of Day Report' },
  { to: '/staff/training', icon: ChartBar, label: 'Training' },
];

const providerLinks = [
  { to: '/provider', icon: House, label: 'Dashboard', end: true },
  { to: '/staff/schedule', icon: CalendarBlank, label: 'Daily Schedule' },
  { to: '/staff/patients', icon: Folders, label: 'Patient Records' },
  { to: '/staff/refills', icon: Pill, label: 'Refill Requests' },
  { to: '/staff/home-labs', icon: Flask, label: 'At-Home Lab Orders' },
  { to: '/provider/health-devices', icon: Watch, label: 'Patient Wearables' },
  { to: '/staff/ai-checkins', icon: Robot, label: 'AI Check-In Reviews' },
  { to: '/provider/encounter-search', icon: MagnifyingGlass, label: 'Encounter Search' },
  { to: '/provider/video-calls', icon: VideoCamera, label: 'Telehealth Video' },
  { to: '/provider/ai-scribe', icon: Microphone, label: 'AI Scribe' },
  { to: '/provider/medications', icon: Pill, label: 'Medications' },
  { to: '/provider/labs', icon: Flask, label: 'Lab Results' },
  { to: '/staff/medicare-forms', icon: Shield, label: 'Medicare Forms' },
  { to: '/provider/prescriptions', icon: Pill, label: 'Prescriptions' },
  { to: '/provider/care-gaps', icon: Warning, label: 'Care Gaps' },
  { to: '/provider/messages', icon: ChatCircle, label: 'Messages' },
  { to: '/provider/notifications', icon: Bell, label: 'Notifications' },
  { to: '/staff/training', icon: ChartBar, label: 'Training' },
];

const adminLinks = [
  { to: '/admin', icon: House, label: 'Dashboard', end: true },
  { to: '/staff/schedule', icon: CalendarBlank, label: 'Daily Schedule' },
  { to: '/staff/patients', icon: Folders, label: 'Patient Records' },
  { to: '/admin/consents', icon: ShieldCheck, label: 'Signed Consents' },
  { to: '/admin/labs-documents', icon: Flask, label: 'Labs & Documents' },
  { to: '/admin/users', icon: Users, label: 'User Management' },
  { 
    label: 'Billing Center', 
    icon: CurrencyDollar, 
    isSubmenu: true,
    children: [
      { to: '/admin/medical-billing', icon: Receipt, label: 'Medical Billing' },
      { to: '/admin/billing', icon: CreditCard, label: 'Invoices' },
      { to: '/admin/claims', icon: PaperPlaneTilt, label: 'Claims' },
      { to: '/admin/denials', icon: XCircle, label: 'Denial Management' },
      { to: '/admin/statements', icon: FileText, label: 'Statements' },
      { to: '/admin/payment-plans', icon: CalendarBlank, label: 'Payment Plans' },
      { to: '/admin/ar-aging', icon: ClockCounterClockwise, label: 'A/R Aging' },
      { to: '/admin/eob-import', icon: Upload, label: 'EOB Import' },
      { to: '/admin/superbills', icon: Receipt, label: 'Superbills' },
    ]
  },
  { to: '/admin/reminders', icon: Bell, label: 'Appointment Reminders' },
  { to: '/admin/reports', icon: FileText, label: 'Reports' },
  { to: '/admin/scheduled-reports', icon: CalendarBlank, label: 'Scheduled Reports' },
  { to: '/admin/telehealth', icon: VideoCamera, label: 'Telehealth Info' },
  { to: '/admin/messages', icon: ChatCircle, label: 'Messages' },
  { to: '/admin/analytics', icon: ChartBar, label: 'Analytics' },
  { to: '/admin/system-health', icon: Heartbeat, label: 'System Health' },
  { to: '/admin/pilot-program', icon: Rocket, label: '8-Week Pilot' },
  { to: '/staff/medicare-forms', icon: Shield, label: 'Medicare Forms' },
  { to: '/admin/care-gaps', icon: Warning, label: 'Care Gaps' },
  { to: '/admin/ai-assistant', icon: Robot, label: 'AI Assistant' },
  { to: '/admin/end-of-day-report', icon: CurrencyDollar, label: 'End of Day Report' },
  { to: '/staff/training', icon: ChartBar, label: 'Training' },
];

const getLinksByRole = (role) => {
  switch (role) {
    case 'patient': return patientLinks;
    case 'cma': return cmaLinks;
    case 'provider': return providerLinks;
    case 'admin': return adminLinks;
    default: return [];
  }
};

const getRoleLabel = (role) => {
  switch (role) {
    case 'patient': return 'Patient Portal';
    case 'cma': return 'CMA Dashboard';
    case 'provider': return 'Provider Dashboard';
    case 'admin': return 'Admin Console';
    default: return 'Dashboard';
  }
};

export default function Sidebar({ onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const links = getLinksByRole(user?.role);
  const [expandedMenus, setExpandedMenus] = useState({});

  // Auto-expand billing menu if we're on a billing page
  useEffect(() => {
    if (location.pathname.includes('/admin/') && 
        (location.pathname.includes('billing') || 
         location.pathname.includes('claims') || 
         location.pathname.includes('statements') ||
         location.pathname.includes('payment-plans') ||
         location.pathname.includes('ar-aging') ||
         location.pathname.includes('eob-import') ||
         location.pathname.includes('superbills') ||
         location.pathname.includes('denials'))) {
      setExpandedMenus(prev => ({ ...prev, 'Billing Center': true }));
    }
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const toggleSubmenu = (label) => {
    setExpandedMenus(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const isChildActive = (children) => {
    return children?.some(child => location.pathname === child.to);
  };

  const isStaffRole = user?.role === 'cma' || user?.role === 'provider';

  return (
    <aside className={`flex flex-col h-full ${isStaffRole ? 'bg-teal-50' : ''}`}>
      {/* Logo */}
      <div className={`p-6 border-b ${isStaffRole ? 'border-teal-200' : 'border-slate-200'}`}>
        <div className="flex items-center gap-2">
          <FirstAidKit size={28} weight="duotone" className="text-teal-600" />
          <div>
            <div className="font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>T-MEDCMA</div>
            <div className="text-xs text-slate-500">{getRoleLabel(user?.role)}</div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {links.map((link) => (
          link.isSubmenu ? (
            <div key={link.label}>
              <button
                onClick={() => toggleSubmenu(link.label)}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`sidebar-item w-full justify-between ${isChildActive(link.children) ? 'sidebar-item-active' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <link.icon size={20} />
                  <span>{link.label}</span>
                </div>
                {expandedMenus[link.label] ? <CaretDown size={16} /> : <CaretRight size={16} />}
              </button>
              {expandedMenus[link.label] && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-slate-200 pl-2">
                  {link.children.map((child) => (
                    <NavLink
                      key={child.to}
                      to={child.to}
                      onClick={onNavigate}
                      data-testid={`nav-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={({ isActive }) =>
                        `sidebar-item text-sm ${isActive ? 'sidebar-item-active' : ''}`
                      }
                    >
                      <child.icon size={16} />
                      <span>{child.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end}
              onClick={onNavigate}
              data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) =>
                `sidebar-item ${isActive ? 'sidebar-item-active' : ''}`
              }
            >
              <link.icon size={20} />
              <span>{link.label}</span>
            </NavLink>
          )
        ))}
      </nav>

      {/* User */}
      <div className="p-4 border-t border-slate-200">
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="w-9 h-9 bg-teal-100 rounded-full flex items-center justify-center">
            <UserCircle size={20} className="text-teal-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm text-slate-900 truncate">
              {user?.first_name} {user?.last_name}
            </div>
            <div className="text-xs text-slate-500">{user?.role === 'cma' ? 'CMA' : user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : ''}</div>
          </div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="w-full sidebar-item text-red-600 hover:bg-red-50 hover:text-red-700 mt-2"
        >
          <SignOut size={20} />
          <span>Sign Out</span>
        </button>
      </div>
    </aside>
  );
}
